<template>
  <div style="min-height: calc(100vh - 176px)" class="flex flex-wrap mt-4">
    <div class="relative py-2 px-2 flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded-lg bg-blueGray-100 border-0" style="min-height: calc(-176px + 100vh);">
      <!-- Filter bar -->
      <div class="bg-white shadow-md p-4 rounded-lg flex flex-wrap gap-4 mb-4">
        <input
          type="text"
          v-model="searchQuery"
          @input="updateFilters"
          placeholder="Поиск по ФИО или логину..."
          class="border p-2 rounded w-full sm:w-1/3"
        />
        <select v-model="sortField" @change="updateFilters" class="border px-3 py-2 pr-7 rounded whitespace-nowrap">
          <option value="failed_count">По неудачным попыткам {{ sortOrder === 'asc' ? '\u2191' : '\u2193' }}</option>
          <option value="last_attempt">По дате последней попытки {{ sortOrder === 'asc' ? '\u2191' : '\u2193' }}</option>
          <option value="user__fio">По ФИО {{ sortOrder === 'asc' ? '\u2191' : '\u2193' }}</option>
          <option value="total_count">По всего попыток {{ sortOrder === 'asc' ? '\u2191' : '\u2193' }}</option>
        </select>
        <button @click="toggleSortOrder" class="border px-3 py-2 rounded bg-gray-100 hover:bg-gray-200 whitespace-nowrap">
          {{ sortOrder === 'asc' ? '\u2191 Возрастание' : '\u2193 Убывание' }}
        </button>
      </div>

      <!-- Table -->
      <div class="bg-white shadow-md rounded-lg overflow-hidden">
        <table class="w-full text-left border-collapse">
          <thead>
            <tr class="bg-gray-200">
              <th class="p-3">#</th>
              <th class="p-3">ФИО</th>
              <th class="p-3">Логин</th>
              <th class="p-3">Неудачных попыток</th>
              <th class="p-3">Всего попыток</th>
              <th class="p-3">Последняя попытка</th>
              <th class="p-3">Действия</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in attemptsList" :key="item.user_id" class="border-t">
              <td class="p-3">{{ index + 1 + (currentPage - 1) * perPage }}</td>
              <td class="p-3">{{ item.fio }}</td>
              <td class="p-3">{{ item.login }}</td>
              <td class="p-3">
                <span class="font-semibold" :class="item.failed_count >= 5 ? 'text-red-600' : 'text-yellow-600'">
                  {{ item.failed_count }}
                </span>
              </td>
              <td class="p-3">{{ item.total_count }}</td>
              <td class="p-3">{{ formatDate(item.last_attempt) }}</td>
              <td class="p-3 flex flex-wrap gap-2">
                <button
                  v-if="item.is_enabled"
                  @click="blockUser(item)"
                  class="bg-red-500 hover:bg-red-600 text-white text-sm px-3 py-1 rounded"
                >
                  Заблокировать
                </button>
                <button
                  v-else
                  @click="unblockUser(item)"
                  class="bg-green-500 hover:bg-green-600 text-white text-sm px-3 py-1 rounded"
                >
                  Разблокировать
                </button>
                <button
                  @click="clearAttempts(item)"
                  class="bg-yellow-500 hover:bg-yellow-600 text-white text-sm px-3 py-1 rounded"
                >
                  Сбросить попытки
                </button>
              </td>
            </tr>
            <tr v-if="attemptsList.length === 0">
              <td colspan="7" class="p-4 text-center text-gray-500">Нет данных о попытках входа</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="flex justify-center mt-4">
        <button
          @click="changePage(currentPage - 1)"
          :disabled="currentPage === 1"
          class="px-4 py-2 bg-gray-300 rounded mr-2"
        >
          &larr; Назад
        </button>
        <span class="px-4 py-2">Страница {{ currentPage }} из {{ totalPages }}</span>
        <button
          @click="changePage(currentPage + 1)"
          :disabled="currentPage >= totalPages"
          class="px-4 py-2 bg-gray-300 rounded ml-2"
        >
          Вперед &rarr;
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { API_LOGIN_ATTEMPTS, HEADERS_TOKEN_JSON } from "@/const/uri";

export default {
  data() {
    return {
      attemptsList: [],
      searchQuery: "",
      currentPage: 1,
      perPage: 10,
      totalPages: 1,
      sortField: "failed_count",
      sortOrder: "desc",
    };
  },
  watch: {
    "$route.query": {
      immediate: true,
      handler() {
        this.fetchAttempts();
      },
    },
  },
  mounted() {
    this.fetchAttempts();
  },
  methods: {
    formatDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    async fetchAttempts() {
      const query = this.$route.query;
      this.searchQuery = query.search || "";
      this.currentPage = Number(query.page) || 1;
      this.sortField = query.sort || "failed_count";
      this.sortOrder = query.order || "desc";

      const orderingField = this.sortOrder === 'desc' ? `-${this.sortField}` : this.sortField;

      try {
        const response = await axios({
          method: "GET",
          url: API_LOGIN_ATTEMPTS.list,
          params: {
            search: this.searchQuery,
            p: this.currentPage,
            page_size: this.perPage,
            ordering: orderingField,
          },
          headers: HEADERS_TOKEN_JSON(),
        });

        if (response.data.code === 0) {
          const responseData = response.data.data;
          this.attemptsList = responseData.results;
          this.totalPages = Math.ceil(responseData.count / this.perPage);
        }
      } catch (e) {
        console.error("Ошибка загрузки попыток входа:", e);
      }
    },
    toggleSortOrder() {
      this.sortOrder = this.sortOrder === "asc" ? "desc" : "asc";
      this.updateFilters();
    },
    updateFilters() {
      this.$router.push({
        query: {
          search: this.searchQuery || undefined,
          sort: this.sortField,
          order: this.sortOrder,
          page: 1,
        },
      });
    },
    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.$router.push({
          query: { ...this.$route.query, page },
        });
      }
    },
    async blockUser(item) {
      if (!confirm(`Заблокировать пользователя ${item.fio}?`)) return;
      try {
        await axios.post(
          `${API_LOGIN_ATTEMPTS.list}${item.user_id}/block/`,
          {},
          { headers: HEADERS_TOKEN_JSON() }
        );
        this.fetchAttempts();
      } catch (e) {
        alert("Ошибка при блокировке");
        console.error(e);
      }
    },
    async unblockUser(item) {
      if (!confirm(`Разблокировать пользователя ${item.fio}?`)) return;
      try {
        await axios.post(
          `${API_LOGIN_ATTEMPTS.list}${item.user_id}/unblock/`,
          {},
          { headers: HEADERS_TOKEN_JSON() }
        );
        this.fetchAttempts();
      } catch (e) {
        alert("Ошибка при разблокировке");
        console.error(e);
      }
    },
    async clearAttempts(item) {
      if (!confirm(`Сбросить неудачные попытки для ${item.fio}?`)) return;
      try {
        await axios.post(
          `${API_LOGIN_ATTEMPTS.list}${item.user_id}/clear-attempts/`,
          {},
          { headers: HEADERS_TOKEN_JSON() }
        );
        this.fetchAttempts();
      } catch (e) {
        alert("Ошибка при сбросе попыток");
        console.error(e);
      }
    },
  },
};
</script>
